import { baseApi } from '@/stores/api';
import { unwrapEnvelopeData } from '@/utils/parseAuthEnvelope';

import type { MenuMePayload, MenuTreeNode } from '../types/menu.types';

/**
 * Trích mảng `menus` từ envelope gateway `{ data: { role, menus, features } }`
 * hoặc tương thích: `data` là mảng trực tiếp, hoặc lồng `data` nhiều lớp.
 */
function extractMenusArray(raw: unknown): MenuTreeNode[] | undefined {
  let cur: unknown = raw;
  for (let depth = 0; depth < 8; depth++) {
    if (cur === null || cur === undefined) return undefined;
    if (Array.isArray(cur)) return cur as MenuTreeNode[];
    if (typeof cur !== 'object') return undefined;
    const o = cur as Record<string, unknown>;
    if (Array.isArray(o.menus)) {
      return o.menus as MenuTreeNode[];
    }
    if ('data' in o && o.data !== undefined) {
      cur = o.data;
      continue;
    }
    break;
  }
  return undefined;
}

/** Peel nested `{ data: ... }` until a menu array (gateway / legacy). */
function extractMenuTreeArrayLegacy(raw: unknown): MenuTreeNode[] {
  let cur: unknown = raw;
  for (let depth = 0; depth < 8; depth++) {
    if (Array.isArray(cur)) return cur as MenuTreeNode[];
    if (cur === null || cur === undefined) return [];
    if (typeof cur !== 'object') return [];
    const o = cur as Record<string, unknown>;
    if ('data' in o && o.data !== undefined) {
      cur = o.data;
      continue;
    }
    break;
  }
  return [];
}

function transformMenuTreeResponse(raw: unknown): MenuTreeNode[] {
  const fromMenusPayload = extractMenusArray(raw);
  if (fromMenusPayload !== undefined) {
    return fromMenusPayload;
  }

  const fromEnvelope = extractMenuTreeArrayLegacy(raw);
  if (fromEnvelope.length > 0) {
    return fromEnvelope;
  }

  const once = unwrapEnvelopeData<unknown>(raw);
  if (Array.isArray(once)) {
    return once as MenuTreeNode[];
  }
  if (Array.isArray(raw)) {
    return raw as MenuTreeNode[];
  }
  return [];
}

function transformMenuMePayload(raw: unknown): MenuMePayload {
  const p = parseMenuMePayload(raw);
  if (p) return p;
  return { role: '', menus: transformMenuTreeResponse(raw), features: {} };
}

/**
 * Trích payload đầy đủ `MenuMePayload` từ body (test / parse thủ công).
 */
export function parseMenuMePayload(raw: unknown): MenuMePayload | null {
  const inner = unwrapEnvelopeData<unknown>(raw);
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) {
    return null;
  }
  const o = inner as Record<string, unknown>;
  if (!Array.isArray(o.menus)) {
    return null;
  }
  const role = typeof o.role === 'string' ? o.role : '';
  const features =
    o.features && typeof o.features === 'object' && !Array.isArray(o.features)
      ? (o.features as MenuMePayload['features'])
      : {};
  return {
    role,
    menus: o.menus as MenuTreeNode[],
    features,
  };
}

export const menusApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Policy đầy đủ từ `GET /api/menus/me`: `role`, cây `menus`, map `features` (F-xxx → boolean).
     * Dùng cho feature-flag; điều hướng có thể chỉ cần `menus` qua {@link useGetMyMenuTreeQuery}.
     */
    getMyMenuPolicy: builder.query<MenuMePayload, void>({
      query: () => ({ url: '/api/menus/me' }),
      transformResponse: transformMenuMePayload,
      providesTags: ['Menu'],
      keepUnusedDataFor: 120,
    }),
  }),
});

export const { useGetMyMenuPolicyQuery } = menusApi;

type MenuTreeQueryOptions = Parameters<typeof useGetMyMenuPolicyQuery>[1];

/**
 * Chỉ `menus` từ {@link getMyMenuPolicy} — tương thích code cũ dùng cây sidebar.
 * Ưu tiên {@link useGetMyMenuPolicyQuery} khi cần `features` / `role`.
 */
export function useGetMyMenuTreeQuery(
  arg?: void,
  options?: Omit<MenuTreeQueryOptions, 'selectFromResult'>,
) {
  return useGetMyMenuPolicyQuery(arg, {
    ...options,
    selectFromResult: (result) => ({
      ...result,
      data: result.data?.menus ?? [],
    }),
  });
}
